/**
 * Month-by-month billed projection for a contract — the ops-side port of Cinch's
 * `buildContractProjection` (employee-portal `contract-value-chart.component.js`).
 * A stepped monthly-billed line with a prorated **P1** stub, **M1..M24** full months,
 * term/renewal **boundaries drawn between periods**, and green **upsell** markers layered
 * from each amendment's effective month.
 *
 * Replacement contracts get a **PC** (Previous Contract) lead-in: up to the last 6 calendar
 * months of the contract they replaced, so the handoff and the value change are visible on
 * one axis. Amendments to the replacement are treated as ordinary upsells.
 *
 * Every column carries a sequential `idx` (0-based) so the chart's numeric X axis can place
 * boundary lines at `idx + 0.5` — between two periods, exactly like the Cinch chart's
 * `xMin/xMax: b.index + 0.5`. Only percentage / fixed / time_based discounts reduce the
 * projection deterministically; tiered / quantity_based are usage-dependent and ignored here.
 */

import type { ContractDetail, ContractDiscount } from "./api";

export const DEFAULT_MONTHS = 24;
const PC_LOOKBACK = 6;
const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export interface ProjectionPoint {
  idx: number; // sequential 0-based, for the numeric X axis
  key: string; // "PC" | "P1" | "M1" …
  calendarLabel: string; // "MMM-YYYY"
  billed: number;
  isStub?: boolean; // the day-prorated P1 first period
  isPrevious?: boolean; // a Previous-Contract (PC) lead-in column
}

/** Drawn BETWEEN periods (at `idx + 0.5`) — term / renewal boundaries. */
export interface Boundary {
  idx: number;
  label: string;
}

/** Drawn ON a column (`idx`) — where an upsell amendment first bills. */
export interface UpsellMarker {
  idx: number;
  label: string;
}

export interface ContractProjection {
  points: ProjectionPoint[];
  boundaries: Boundary[];
  upsellMarkers: UpsellMarker[];
  baseMonthly: number;
  /** Total billed across the contract's own columns (P1 + M1..Mn), excluding PC lead-in. */
  total: number;
  /** P1 stub (if any) + M1..M12 — the contract-aligned "first 12 months". */
  firstYear: number;
  secondYear: number;
  hasStub: boolean;
  /** Count of leading PC (previous-contract) columns, 0 for a normal contract. */
  pcCount: number;
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

interface YM {
  y: number;
  m: number; // 1-based month
}

function parseYmd(iso: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d };
}

const ordinal = (ym: YM): number => ym.y * 12 + (ym.m - 1);
const fromOrdinal = (o: number): YM => ({ y: Math.floor(o / 12), m: (o % 12) + 1 });
const addMonths = (ym: YM, delta: number): YM => fromOrdinal(ordinal(ym) + delta);
const monthLabel = (ym: YM): string => `${MONTH_ABBR[ym.m - 1]}-${ym.y}`;
const daysInMonth = (ym: YM): number => new Date(ym.y, ym.m, 0).getDate();

/** A raw (pre-index) column produced by the single-contract engine. */
interface Col {
  key: string;
  ym: YM | null;
  monthNum?: number; // 1-based full-month number (absent for the P1 stub)
  billed: number;
  isStub?: boolean;
}

interface EngineResult {
  columns: Col[];
  /** boundaries/markers as indices INTO `columns` (before any PC prepend shifts them). */
  boundaries: { localIdx: number; label: string }[];
  upsellMarkers: { localIdx: number; label: string }[];
  baseMonthly: number;
  hasStub: boolean;
}

/** Build one contract's P1?/M1..Mmonths columns + boundary/upsell markers (Cinch parity).
 *  `prorate=false` suppresses the P1 stub (replacement contracts don't prorate — they bill
 *  a full M1 from their effective month). */
function engine(contract: ContractDetail, months: number, prorate = true): EngineResult {
  const lineItems = contract.contract_line_items ?? [];
  const lineItemsTotal = lineItems.reduce((s, l) => s + num(l.item_total), 0);
  const baseMonthly = lineItemsTotal + num(contract.base_fee);
  const term = num(contract.term_months);
  const autoRenew = Boolean(contract.is_auto_renew);
  const renewalTerm = contract.renewal_term ?? "SAME_TERM";
  const mtmUplift = num(contract.mtm_uplift_pct);

  const lineTotalById: Record<string, number> = {};
  for (const l of lineItems) if (l.uuid) lineTotalById[l.uuid] = num(l.item_total);

  // Each discount → monthly $ reduction + active full months (from M1; null = life).
  const windows = (contract.contract_discounts ?? []).map((cd: ContractDiscount) => {
    const d = cd.discount_detail ?? {};
    const base = cd.target_line_item ? lineTotalById[cd.target_line_item] || 0 : baseMonthly;
    let monthlyReduction = 0;
    if (d.type === "percentage" || d.type === "time_based") monthlyReduction = (base * num(d.value)) / 100;
    else if (d.type === "fixed") monthlyReduction = num(d.value);
    const duration =
      cd.duration_override != null
        ? num(cd.duration_override)
        : d.type === "time_based" && d.duration_periods != null
          ? num(d.duration_periods)
          : null;
    return { monthlyReduction, duration };
  });

  const reductionForMonth = (m: number) =>
    windows.reduce((s, w) => s + (w.duration == null || m <= w.duration ? w.monthlyReduction : 0), 0);

  const fullMonthBilled = (m: number) => {
    if (term > 0 && !autoRenew && m > term) return 0; // expired, not renewed
    let base = baseMonthly;
    if (autoRenew && renewalTerm === "MONTH_TO_MONTH" && term > 0 && m > term) {
      base = base * (1 + mtmUplift / 100);
    }
    return Math.max(0, base - reductionForMonth(m));
  };

  const stubFraction = (ymd: { m: number; d: number; y: number }) => {
    const dim = daysInMonth(ymd);
    return (dim - ymd.d + 1) / dim;
  };

  const eff = parseYmd(contract.billing_effective_from);
  const hasStub = prorate && !!eff && eff.d !== 1;
  const effMonth: YM | null = eff ? { y: eff.y, m: eff.m } : null;
  const firstFullMonth: YM | null = effMonth
    ? hasStub
      ? addMonths(effMonth, 1)
      : effMonth
    : null;

  const round2 = (x: number) => Math.round(x * 100) / 100;
  const columns: Col[] = [];
  if (hasStub && eff && effMonth) {
    const stubBilled = Math.max(0, (baseMonthly - reductionForMonth(1)) * stubFraction(eff));
    columns.push({ key: "P1", ym: effMonth, billed: round2(stubBilled), isStub: true });
  }
  for (let m = 1; m <= months; m++) {
    columns.push({
      key: `M${m}`,
      monthNum: m,
      ym: firstFullMonth ? addMonths(firstFullMonth, m - 1) : null,
      billed: round2(fullMonthBilled(m)),
    });
  }

  // Layer in upsell amendments (each carries mrr_total + billing_effective_from +
  // contract_type_name via ContractSimpleSerializer). Prorate the first month if mid-month.
  const upsellMarkers: { localIdx: number; label: string }[] = [];
  for (const am of contract.amended_by_contracts ?? []) {
    const amMonthly = num(am.mrr_total);
    const amEff = parseYmd(am.billing_effective_from);
    if (!amMonthly || !amEff) continue;
    const amEffMonth: YM = { y: amEff.y, m: amEff.m };
    const amHasStub = amEff.d !== 1;
    const amFirstFull = amHasStub ? addMonths(amEffMonth, 1) : amEffMonth;
    let firstIdx = -1;
    columns.forEach((col, idx) => {
      if (!col.ym) return;
      let add = 0;
      if (amHasStub && ordinal(col.ym) === ordinal(amEffMonth)) add = amMonthly * stubFraction(amEff);
      else if (ordinal(col.ym) >= ordinal(amFirstFull)) add = amMonthly;
      if (add > 0) {
        col.billed = round2(col.billed + add);
        if (firstIdx < 0) firstIdx = idx;
      }
    });
    if (firstIdx >= 0) upsellMarkers.push({ localIdx: firstIdx, label: am.contract_type_name || "Upsell" });
  }

  // Term / renewal boundaries, anchored to the full-month clock (M1 = term month 1).
  const idxOfMonthNum = (m: number) => columns.findIndex((c) => c.monthNum === m);
  const boundaries: { localIdx: number; label: string }[] = [];
  if (term > 0) {
    if (!autoRenew) {
      boundaries.push({ localIdx: idxOfMonthNum(term), label: "Term ends" });
    } else if (renewalTerm === "MONTH_TO_MONTH") {
      boundaries.push({ localIdx: idxOfMonthNum(term), label: "Month-to-month" });
    } else {
      const cycle =
        renewalTerm === "DIFFERENT_TERM" && num(contract.renewal_term_months) > 0
          ? num(contract.renewal_term_months)
          : term;
      let b = term;
      let n = 1;
      while (b < months) {
        boundaries.push({ localIdx: idxOfMonthNum(b), label: `Renewal ${n}` });
        b += cycle;
        n += 1;
      }
    }
  }

  return { columns, boundaries, upsellMarkers, baseMonthly, hasStub };
}

/** Sum + first/second-year totals over a contract's own columns (P1 + M1..). */
function summarize(columns: Col[]): { total: number; firstYear: number; secondYear: number } {
  let total = 0;
  let firstYear = 0;
  let secondYear = 0;
  for (const c of columns) {
    total += c.billed;
    if (c.isStub || (c.monthNum != null && c.monthNum <= 12)) firstYear += c.billed;
    if (c.monthNum != null && c.monthNum >= 13 && c.monthNum <= 24) secondYear += c.billed;
  }
  return { total, firstYear, secondYear };
}

/** Assemble the final projection from a contract's own columns (no PC lead-in). */
export function buildContractProjection(
  contract: ContractDetail,
  months: number = DEFAULT_MONTHS,
  prorate = true,
): ContractProjection {
  const r = engine(contract, months, prorate);
  const points: ProjectionPoint[] = r.columns.map((c, idx) => ({
    idx,
    key: c.key,
    calendarLabel: c.ym ? monthLabel(c.ym) : "",
    billed: c.billed,
    isStub: c.isStub,
  }));
  const { total, firstYear, secondYear } = summarize(r.columns);
  return {
    points,
    boundaries: r.boundaries.filter((b) => b.localIdx >= 0).map((b) => ({ idx: b.localIdx, label: b.label })),
    upsellMarkers: r.upsellMarkers.map((u) => ({ idx: u.localIdx, label: u.label })),
    baseMonthly: r.baseMonthly,
    total,
    firstYear,
    secondYear,
    hasStub: r.hasStub,
    pcCount: 0,
  };
}

/**
 * Replacement projection: up to the last 6 calendar months of `previous` (the replaced
 * contract, valued from its own engine so its discounts/upsells are honored), labeled PC,
 * then the replacement's own P1/M1..M24 with its boundaries + upsell markers. When the
 * previous contract billed fewer than 6 months before the handoff, only those are shown.
 */
export function buildReplacementProjection(
  replacement: ContractDetail,
  previous: ContractDetail | null | undefined,
  months: number = DEFAULT_MONTHS,
): ContractProjection {
  // Replacements don't prorate — no P1 stub; M1 bills full from the effective month.
  const self = buildContractProjection(replacement, months, false);
  const replEff = parseYmd(replacement.billing_effective_from);
  if (!previous || !replEff) return self;

  const replOrd = ordinal({ y: replEff.y, m: replEff.m });
  // Project the previous contract far enough forward to reach the handoff — a long-running
  // contract (e.g. signed years before the replacement) needs many months to cover the
  // lookback window. Then pick the calendar months in [replEff-6, replEff-1] it billed.
  const prevStart = parseYmd(previous.billing_effective_from);
  const prevStartOrd = prevStart ? ordinal({ y: prevStart.y, m: prevStart.m }) : replOrd - PC_LOOKBACK;
  const prevMonths = Math.min(360, Math.max(PC_LOOKBACK, replOrd - prevStartOrd + 1));
  const prev = engine(previous, prevMonths);
  const pcCols = prev.columns
    .filter((c) => c.ym != null && c.billed > 0)
    .filter((c) => {
      const o = ordinal(c.ym as YM);
      return o >= replOrd - PC_LOOKBACK && o < replOrd;
    })
    .sort((a, b) => ordinal(a.ym as YM) - ordinal(b.ym as YM))
    .slice(-PC_LOOKBACK);

  if (pcCols.length === 0) return self;

  const pcPoints: ProjectionPoint[] = pcCols.map((c, i) => ({
    idx: i,
    key: "PC",
    calendarLabel: c.ym ? monthLabel(c.ym) : "",
    billed: c.billed,
    isPrevious: true,
  }));
  const shift = pcPoints.length;
  const shifted = self.points.map((p) => ({ ...p, idx: p.idx + shift }));

  return {
    ...self,
    points: [...pcPoints, ...shifted],
    boundaries: self.boundaries.map((b) => ({ ...b, idx: b.idx + shift })),
    upsellMarkers: self.upsellMarkers.map((u) => ({ ...u, idx: u.idx + shift })),
    pcCount: shift,
  };
}
